/**
 * AttendanceFunnel Component
 * Shows today's attendance flow as a compact funnel with progress bars
 */
import React from 'react';
import { Card, CardContent, Typography, Box, LinearProgress, Chip, Divider } from '@mui/material';
import { Timeline } from '@mui/icons-material';
import { AttendanceSummary } from '../../../types/dashboard';

interface AttendanceFunnelProps {
  data: AttendanceSummary;
  loading?: boolean;
}

const FunnelRow: React.FC<{
  label: string;
  value: number;
  total: number;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}> = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>
          {value} <Typography component="span" variant="caption" color="text.secondary">({pct}%)</Typography>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};

const AttendanceFunnel: React.FC<AttendanceFunnelProps> = ({ data, loading }) => {
  if (loading) return null;

  const { today, thisWeek } = data;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Timeline color="primary" />
          Atendimentos do Dia
        </Typography>

        {today.total === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Nenhum agendamento para hoje
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Chip label={`${today.total} agendados`} size="small" variant="outlined" />
            </Box>

            <FunnelRow label="Aguardando" value={today.pending} total={today.total} color="info" />
            <FunnelRow label="Check-in / Triagem" value={today.checkedIn} total={today.total} color="primary" />
            <FunnelRow label="Em atendimento" value={today.inProgress} total={today.total} color="warning" />
            <FunnelRow label="Concluídos" value={today.completed} total={today.total} color="success" />
            {today.noShow > 0 && (
              <FunnelRow label="Não compareceram" value={today.noShow} total={today.total} color="error" />
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Semana</Typography>
            <Typography variant="body2" fontWeight={600}>{thisWeek.completed}/{thisWeek.total} concluídos</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">No-show</Typography>
            <Typography variant="body2" fontWeight={600} color={thisWeek.noShowRate > 15 ? 'error.main' : 'text.primary'}>
              {thisWeek.noShowRate}%
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AttendanceFunnel;
